/* JavaScript for main site */

//
// functions
//

/**
 * Display snackbar and remove it after defined delay period (ms).
 * @param snackbar {HTMLElement} Snackbar element to display.
 * @param delay {Number} Time (ms) before removing snackbar.
 */
function displaySnackbar(snackbar, delay = 7000) {
    // set dataset-state to show snackbar
    snackbar.dataset.state = "show";

    // auto-hide snackbar after 'delay' ms
    setTimeout(() => {
        snackbar.dataset.state = "hide"
    }, delay);
}

/**
 * Asynchronous timeout.
 * @param ms {Number} Number of milliseconds to countdown.
 * @returns {Promise<unknown>} Promise always resolves upon countdown termination.
 */
const asyncWait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Checks the state of the 'prefers-reduced-motion' browser user-setting.
 * @returns {boolean} True if enabled, False otherwise.
 */
const checkReducedMotionPreference = () => {
    const mediaQueryResult = window.matchMedia("(prefers-reduced-motion: reduce)");
    matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", checkReducedMotionPreference, {once: true});
    return !(!mediaQueryResult || !mediaQueryResult.matches)
}

/**
 * Scroll to an anchor with the option to use smooth-scrolling.
 * @param target {Location, EventTarget} Anchor location as scrolling destination.
 * @param smooth {Boolean} Whether or not to implement smooth-scrolling.
 */
function doScroll(target, smooth = true) {
    // handle null targets like back-to-top button
    if (target.hash === "") {
        document.documentElement.scrollIntoView({behavior: smooth ? "smooth" : "auto"});
        window.history.replaceState(null, '', ' ');
        return;
    }

    // handle all the other targets
    const anchorName = target.hash.replace('#', '');
    const anchor = document.getElementById(anchorName);
    if (!anchor) return;

    anchor.scrollIntoView({
        behavior: smooth ? "smooth" : "auto", block: "start", inline: "nearest"
    });
    const locationRoot = window.location.toString().split('#')[0];
    window.history.replaceState(null, '', `${locationRoot}#${anchorName}`);
}

/**
 * Compare two urls to determine if they are the same. By default, the second url (comparator) is the current location.
 * @param url1 {URL, string} URL to compare against comparator.
 * @param url2 {URL, string, boolean} URL to act as comparator. If false, current window URL will be used.
 * @returns {boolean} True if URLs match, False otherwise.
 */
function isSamePage(url1, url2 = false) {
    if (!url2) url2 = window.location.href;
    url1 = new URL(url1);
    url2 = new URL(url2);

    if (url1.protocol !== url2.protocol) return false;
    if (url1.host !== url2.host) return false;
    if (url1.port !== url2.port) return false;
    return url1.pathname === url2.pathname;
}

/**
 * Redefine ViewPoint Height unit (vh) based on current window innerHeight.
 */
function redefineVhUnit() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
}

/**
 * Process data-analytics property and inform Plausible Analytics to update web statistics.
 * @param data {String} Comma-delimited array of attributes to submit.
 */
function updatePlausible(data) {
    if (typeof plausible == "undefined") return;
    let attributes = data.split(/,(.+)/);
    let events = [JSON.parse(attributes[0]), JSON.parse(attributes[1] || '{}')];
    plausible(...events);
}

//
// event-driven actions
//

// compensate for vertical viewpoint unit issues especially on mobile
redefineVhUnit();
window.addEventListener("resize", () => {
    redefineVhUnit();
}, false);

// override default link action and set click handler for all links
let prefersReducedMotion = checkReducedMotionPreference();
const allLinks = document.querySelectorAll("a:not([data-pseudo-link])");
allLinks.forEach(link => {
    link.addEventListener("click", async evt => {
        evt.preventDefault();

        const plausibleWaitPeriod = 500;
        let updatingPlausible = false;
        let target = evt.target;
        // walk up the DOM from our 'evt' until an 'a' element is found for the target
        // noinspection JSUnresolvedVariable
        while (target && (typeof target.tagName == "undefined" || target.tagName.toLocaleLowerCase() !== "a" || !target.href)) {
            // noinspection JSUnresolvedVariable
            target = target.parentNode;
        }

        // update Plausible as necessary
        // noinspection JSUnresolvedFunction
        if (target.getAttribute("data-analytics")) {
            updatingPlausible = true;
            // noinspection JSUnresolvedFunction
            updatePlausible(target.getAttribute("data-analytics"));
        }

        // process link
        // noinspection JSUnresolvedVariable
        if (isSamePage(target.href)) {
            // link is an anchor: do smooth-scroll if 'prefer-reduced-motion' is NOT set
            doScroll(target, !prefersReducedMotion);
            return;
        }
        // link is external: check if updating Plausible and delay processing for a bit to let that happen
        if (updatingPlausible) await asyncWait(plausibleWaitPeriod);
        // noinspection JSUnresolvedVariable
        window.location.href = target.href;
    }, false);
});

// handle menu tasks without requiring location hash changes (cleaner experience)
// N.B. these links should have the custom property 'data-pseudo-link' so the default click handler ignores them!
const btnOpenMenu = document.getElementById("main-nav-toggle-open");
const btnCloseMenu = document.getElementById("main-nav-toggle-close");
const navMenu = document.getElementById("main-navigation");
const navMenuLinkItems = document.querySelectorAll(".main-navigation-list-item");

// override menu open (prevent adding hash fragment) and open navigation menu
btnOpenMenu.addEventListener("click", evt => {
    evt.preventDefault();
    navMenu.setAttribute("data-visible", "true");
    document.body.style.overflowY = "hidden"; // prevent scrolling body while menu open
}, false);

// override menu close (prevent adding hash fragment) and close navigation menu
btnCloseMenu.addEventListener("click", evt => {
    evt.preventDefault();
    navMenu.setAttribute("data-visible", "false");
    document.body.style.overflowY = "auto"; // re-enable y-axis scrolling on body
}, false);

// iterate all menu links and close the menu if any are clicked
if (navMenuLinkItems) {
    navMenuLinkItems.forEach(navLink => {
        navLink.addEventListener("click", () => {
            navMenu.setAttribute("data-visible", "false");
            document.body.style.overflowY = "auto"; // re-enable y-axis scrolling on body
        }, false);
    });
}

// highlight currently visible section on menu (full menu only)
if (window.innerWidth >= 768) {
    const sections = document.querySelectorAll("section");
    const navMenuItems = document.querySelectorAll(".menu-link");
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.target.id === "") return;
            const correspondingMenuItem = Array.from(navMenuItems).find((item) => item.hash.replace("#", "") === entry.target.id);
            if (correspondingMenuItem) {
                if (entry.isIntersecting) {
                    correspondingMenuItem.classList.add("onScreen");
                }
                else {
                    correspondingMenuItem.classList.remove("onScreen");
                }
            }
        });
    }, {rootMargin: "-20%"});

// observe section elements
    sections.forEach(section => {
        observer.observe(section);
    });
}

//
// mailing list registration operations
//

const backendRegistrationServer = document.body.dataset.registration;
const productName = document.body.dataset.product;
const processingOverlay = document.getElementById("processingOverlay");
const snackbar = document.getElementById("snackbar");
const snackbarSubject = document.getElementById("snackbar-subject");
const snackbarContent = document.getElementById("snackbar-content");
const registrationForm = document.getElementById("registrationForm");

if (registrationForm !== null) {
    registrationForm.addEventListener("submit", function (e) {
        // we'll handle form submission via this script instead of passing to the default 'action' handler
        e.preventDefault();

        // assemble form data into an object for submission
        const formData = new FormData(this);
        const formDataObject = Object.fromEntries(formData);
        formDataObject.productName = productName;

        // activate processing-overlay spinner and submit search params form data to backend server
        processingOverlay.style.display = "block";
        fetch(backendRegistrationServer, {
            method: 'post', body: JSON.stringify(formDataObject), headers: {
                "Content-Type": "application/json"
            }
        }).then(
            // convert to text so that null check can be performed in the case of code 204: no content (for example)
            response => response.text()
        ).then(
            responseText => {
                if (responseText)
                    return JSON.parse(responseText); // response body is non-null, convert to JSON
                return {
                    success: false,
                    message: "Please try again later.",
                    technicalMessage: "Empty response from backend."
                }
            }
        ).then(result => {
            // clear processing overlay spinner and set status text based on backend response
            processingOverlay.style.display = "none";
            if (result.success) {
                snackbarSubject.innerText = "SUCCESS";
                snackbarContent.innerText = result.message;
                snackbar.classList.add("success");
                displaySnackbar(snackbar);

                if (result.technicalMessage) console.log("registration:", result.technicalMessage);
            }
            else {
                const err = new Error();
                err.message = result.message;
                err.technicalMessage = result.technicalMessage;
                throw err;
            }
        }).catch(error => {
            // clear processing overlay spinner and set status text based on error
            processingOverlay.style.display = "none";
            snackbarSubject.innerText = "ERROR";
            snackbarContent.innerText = error.message;
            snackbar.classList.add("error");
            displaySnackbar(snackbar);

            if (error.technicalMessage) console.error(error.technicalMessage);
        });
    });
}
